/**
 * Color Family Filter Component
 *
 * Checkbox list with color swatches.
 * Figma: Filter Section - Color Family (Node: 358:18135)
 *
 * Features: Color swatches, checkboxes, color names
 */
"use client";

import { useState } from "react";

import { colors } from "@/styles/design-tokens";

import { Checkbox } from "./Checkbox";
import { FilterCategoryHeading } from "./FilterCategoryHeading";

export interface ColorOption {
  id: string;
  label: string;
  hex: string;
}

interface ColorFamilyFilterProps {
  colors: readonly ColorOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  counts?: Map<string, number>;
  className?: string;
}

export function ColorFamilyFilter({
  colors: colorOptions,
  selected,
  onChange,
  counts,
  className = "",
}: ColorFamilyFilterProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleColor = (colorId: string) => {
    const count = counts?.get(colorId) ?? 0;
    if (counts && count <= 0 && !selected.includes(colorId)) {
      return;
    }

    const newSelected = selected.includes(colorId)
      ? selected.filter((id) => id !== colorId)
      : [...selected, colorId];
    onChange(newSelected);
  };

  return (
    <div className={className}>
      {/* Heading */}
      <FilterCategoryHeading
        label="COLOR FAMILY"
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
      />

      {/* Color List */}
      {isExpanded && (
        <div
          style={{
            marginTop: "21.983px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {colorOptions.map((color, index) => {
            const isSelected = selected.includes(color.id);
            const count = counts?.get(color.id) ?? 0;
            const isDisabled = Boolean(counts && count <= 0 && !isSelected);
            return (
              <div
                key={color.id}
                onClick={() => toggleColor(color.id)}
                aria-disabled={isDisabled}
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  height: "23.988px",
                  marginTop: index > 0 ? "10px" : "0px", // 10px gap between items
                  cursor: isDisabled ? "default" : "pointer",
                  opacity: isDisabled ? 0.4 : 1,
                }}
              >
                {/* Checkbox - 12px × 12px */}
                <div
                  style={{
                    paddingTop: "3.996px",
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => toggleColor(color.id)}
                    variant="invisible"
                  />
                </div>

                {/* Color swatch and label */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginLeft: "23.988px", // Gap between checkbox and content (35.988 - 12)
                    paddingTop: "4.654px",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {/* Color swatch */}
                  <div
                    style={{
                      width: "6.986px",
                      height: "6.986px",
                      borderRadius: "50%",
                      backgroundColor: color.hex,
                      border: `1px solid ${colors.divider.light}`,
                      flexShrink: 0,
                    }}
                  />

                  {/* Label */}
                  <span className="text-action text-ink truncate">{color.label}</span>
                </div>
                {counts && (
                  <span className="text-caption text-ink-secondary tabular-nums">
                    {count.toLocaleString()}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Divider */}
      <div
        style={{
          width: "100%",
          height: "0.882px",
          backgroundColor: colors.divider.light,
          marginTop: isExpanded ? "15px" : "0px",
        }}
      />
    </div>
  );
}
